import { MainLayout } from "@/components/layout/MainLayout"
import { Button } from "@/components/ui/button"
import { CasesList } from "@/components/cases/CasesList"
import { useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { PlusCircle, Folders, FileText, Calendar } from "lucide-react"
import { useCases } from "../hooks/useCases"
import { useDashboardStats } from "../hooks/useDashboardStats"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { CasesTimeSeriesChart } from "@/components/dashboard/CasesTimeSeriesChart"
import { DocumentsPerCaseCard } from "@/components/dashboard/DocumentsPerCaseCard"

const Dashboard = () => {
	const navigate = useNavigate()
	const { data: cases, isLoading: casesLoading, error: casesError } = useCases()
	const [timePeriod, setTimePeriod] = useState<number>(7)
	const { data: dashboardStats, isLoading: statsLoading, error: statsError } = useDashboardStats(timePeriod)

	const totalDocuments = useMemo(() => {
		return dashboardStats?.total_documents ?? 0
	}, [dashboardStats])

	const stats = [
		{
			name: "Total Documents",
			value: totalDocuments,
			icon: <FileText className="h-5 w-5 text-primary" />,
		},
		{
			name: timePeriod === 0 ? "All Cases" : `Cases (${timePeriod} days)`,
			value: dashboardStats?.recent_cases ?? 0,
			icon: <Folders className="h-5 w-5 text-primary" />,
		},
	]

	const isLoading = casesLoading || statsLoading
	const error = casesError || statsError

	if (isLoading) {
		return (
			<MainLayout>
				<div>Loading dashboard data...</div>
			</MainLayout>
		)
	}

	if (error) {
		return (
			<MainLayout>
				<div>Error loading data: {error.message}</div>
			</MainLayout>
		)
	}

	if (!cases) {
		return (
			<MainLayout>
				<div>No cases data available.</div>
			</MainLayout>
		)
	}

	return (
		<MainLayout>
			<section className="mb-10 animate-fade-in">
				<div className="flex items-end justify-between mb-6">
					<div>
						<h1 className="text-3xl font-semibold text-foreground">
							Dashboard
						</h1>
						<p className="text-muted-foreground mt-1">
							Welcome to Expert Neurological Case Management
						</p>
					</div>
					<Button onClick={() => navigate("/cases/new")}>
						<PlusCircle className="h-4 w-4 mr-2" />
						New Case
					</Button>
				</div>

				<div className="flex justify-end mb-4">
					<div className="flex items-center gap-2">
						<Calendar className="h-4 w-4 text-muted-foreground" />
						<span className="text-sm text-muted-foreground">Period:</span>
						<Select
							value={timePeriod.toString()}
							onValueChange={(value) => setTimePeriod(Number(value))}
						>
							<SelectTrigger className="w-[120px]">
								<SelectValue placeholder="Last 7 days" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="0">All time</SelectItem>
								<SelectItem value="7">Last 7 days</SelectItem>
								<SelectItem value="30">Last 30 days</SelectItem>
								<SelectItem value="90">Last 90 days</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
					{stats.map((stat) => (
						<div key={stat.name} className="glass p-6 rounded-xl">
							<div className="flex items-center space-x-4">
								<div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
									{stat.icon}
								</div>
								<div>
									<p className="text-sm text-muted-foreground">{stat.name}</p>
									<p className="text-3xl font-semibold">{stat.value}</p>
								</div>
							</div>
						</div>
					))}
				</div>
				
				{/* Cases Time Series Chart */}
				<div className="mb-8">
					<CasesTimeSeriesChart />
				</div>

				{/* Documents Per Case Card */}
				<div className="mb-8">
					<DocumentsPerCaseCard days={timePeriod} />
				</div>
			</section>

			<section className="mb-10 animate-slide-in">
				<div className="flex items-center justify-between mb-6">
					<h2 className="text-xl font-semibold text-foreground">
						Recent Cases
					</h2>
					<Button variant="ghost" onClick={() => navigate("/cases")}>
						View all
					</Button>
				</div>
				<CasesList cases={cases.slice(0, 3)} />
			</section>
		</MainLayout>
	)
}

export default Dashboard
